"use client";

import { useRef, useState } from "react";
import { cn } from "~/lib/utils";

interface TherapistVideoPreviewProps {
  src: string;
  className?: string;
  /**
   * Introductions arrive as 1:1, 16:9 and 9:16. "contain" shows the whole
   * frame; "cover" fills a fixed shape such as a circular avatar.
   */
  fit?: "contain" | "cover";
}

/**
 * A silent preview for pointer devices. The full introduction remains an
 * explicit action so cards never unexpectedly play audio or steal focus.
 */
export function TherapistVideoPreview({
  src,
  className,
  fit = "contain",
}: TherapistVideoPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const startPreview = async () => {
    if (
      !window.matchMedia("(hover: hover)").matches ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setIsPreviewing(true);

    try {
      await video.play();
    } catch {
      setIsPreviewing(false);
    }
  };

  const stopPreview = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setIsPreviewing(false);
  };

  return (
    <div
      aria-hidden="true"
      className={cn("absolute inset-0 overflow-hidden", className)}
      onMouseEnter={startPreview}
      onMouseLeave={stopPreview}
    >
      <video
        ref={videoRef}
        className={cn(
          "h-full w-full opacity-0 transition-opacity duration-300",
          fit === "cover" ? "object-cover" : "object-contain",
          isPreviewing && "bg-primary opacity-100",
        )}
        loop
        muted
        playsInline
        preload="metadata"
        src={src}
      />
    </div>
  );
}
